import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Camera, Upload, Loader2, Check, MessageCircle, Package,
  Home as HomeIcon, MapPin, Building2, Calendar as CalIcon, User as UserIcon, Trash2, Clock,
} from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { api, fileUrl, BACKEND_URL } from "../lib/api";
import { buildWhatsappMessage, whatsappLink, buildUmzugWaMessage, WHATSAPP_NUMBER as FALLBACK_WA } from "../lib/whatsapp";
import { track, trackStd } from "../lib/pixel";
import { toast } from "sonner";

const STEPS = [
  { label: "Adressen", icon: MapPin },
  { label: "Etagen", icon: Building2 },
  { label: "Termin", icon: CalIcon },
  { label: "Fotos", icon: Camera },
  { label: "Kontakt", icon: UserIcon },
  { label: "Angebot", icon: Check },
];

const TIME_SLOTS = ["08:00", "09:00", "10:00", "12:00", "14:00", "16:00", "18:00"];
const DRAFT_KEY = "leukas_quote_draft_v1";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function Quote() {
  const query = useQuery();
  const initialType = query.get("type") === "full" ? "full" : "small";
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [type, setType] = useState(initialType);
  const [waNumber, setWaNumber] = useState(FALLBACK_WA);

  const [form, setForm] = useState({
    pickup_address: "",
    delivery_address: "",
    pickup_floor: 0,
    pickup_elevator: false,
    delivery_floor: 0,
    delivery_elevator: false,
    moving_date: "",
    moving_time: "",
    time_window: "",
    first_name: "",
    last_name: "",
    phone: "",
    whatsapp: "",
    photos: [], // {id, url, name}
    detected_items: [],
    job_type: "",
    effort: "",
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [createdQuote, setCreatedQuote] = useState(null);
  const [draftLoaded, setDraftLoaded] = useState(false);

  // Restore draft from localStorage on mount + scan prefill from sessionStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && typeof saved === "object") {
          if (saved.form) setForm((prev) => ({ ...prev, ...saved.form }));
          if (typeof saved.step === "number") setStep(Math.min(STEPS.length - 1, Math.max(0, saved.step)));
          if (saved.type === "small" || saved.type === "full") setType(saved.type);
        }
      }
    } catch {}
    try {
      const pfRaw = sessionStorage.getItem("scan_prefill_v1");
      if (pfRaw) {
        const pf = JSON.parse(pfRaw);
        if (pf && typeof pf === "object") {
          if (pf.type === "small" || pf.type === "full") setType(pf.type);
          const patch = {};
          if (Array.isArray(pf.detected_items) && pf.detected_items.length > 0) {
            patch.detected_items = pf.detected_items;
          }
          if (typeof pf.job_type === "string" && pf.job_type) patch.job_type = pf.job_type;
          if (typeof pf.effort === "string" && pf.effort) patch.effort = pf.effort;
          if (Object.keys(patch).length > 0) {
            setForm((prev) => ({ ...prev, ...patch }));
          }
        }
        sessionStorage.removeItem("scan_prefill_v1");
      }
    } catch {}
    setDraftLoaded(true);
  }, []);

  // Pixel: fire QuoteStarted (custom) on first mount of the wizard
  useEffect(() => {
    track({ custom: "QuoteStarted", params: { type: initialType } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pixel: fire QuoteCompleted when user reaches the final price screen
  useEffect(() => {
    if (step === STEPS.length - 1 && estimate) {
      track({
        custom: "QuoteCompleted",
        params: { type, estimate_min: estimate.min, estimate_max: estimate.max },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, estimate?.min, estimate?.max]);

  // Auto-save draft on changes (skip until restored to avoid wiping)
  useEffect(() => {
    if (!draftLoaded) return;
    if (createdQuote) return; // don't keep saving after submission
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, step, type }));
    } catch {}
  }, [form, step, type, draftLoaded, createdQuote]);

  useEffect(() => {
    api.get("/config").then(({ data }) => {
      if (data?.whatsapp_number) setWaNumber(data.whatsapp_number);
    }).catch(() => {});
  }, []);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const canNext = () => {
    if (step === 0) return form.pickup_address.trim().length > 2 && form.delivery_address.trim().length > 2;
    if (step === 1) return Number.isFinite(+form.pickup_floor) && Number.isFinite(+form.delivery_floor);
    if (step === 2) return !!form.moving_date;
    if (step === 3) return true; // photos optional
    if (step === 4) return form.first_name.trim() && form.last_name.trim() && form.phone.trim().length >= 5;
    return true;
  };

  const fetchEstimate = async () => {
    try {
      const { data } = await api.post("/estimate", {
        type,
        pickup_address: form.pickup_address,
        delivery_address: form.delivery_address,
        pickup_floor: +form.pickup_floor || 0,
        pickup_elevator: form.pickup_elevator,
        delivery_floor: +form.delivery_floor || 0,
        delivery_elevator: form.delivery_elevator,
        photo_count: form.photos.length,
      });
      setEstimate(data);
    } catch {
      toast.error("Schätzung fehlgeschlagen");
    }
  };

  const next = async () => {
    if (!canNext()) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }
    if (step === 4) {
      await fetchEstimate();
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const onPickFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ id: data.id, url: data.url, name: file.name });
      } catch (e) {
        toast.error(`Upload fehlgeschlagen: ${file.name}`);
      }
    }
    setUploading(false);
    update({ photos: [...form.photos, ...uploaded].slice(0, 15) });
  };

  const removePhoto = (id) => {
    update({ photos: form.photos.filter((p) => p.id !== id) });
  };

  const submitQuote = () => {
    // ---- STEP 1: build wa.me URL from current form values ----
    const digits = (waNumber || FALLBACK_WA || "").toString().replace(/\D/g, "");
    const waUrl = whatsappLink(digits, buildUmzugWaMessage({
      first_name: form.first_name,
      last_name: form.last_name,
      phone: form.phone,
      email: form.email || "",
      pickup_address: form.pickup_address,
      delivery_address: form.delivery_address,
      pickup_floor: form.pickup_floor,
      delivery_floor: form.delivery_floor,
      moving_date: form.moving_date,
      moving_time: form.moving_time,
      notes: form.notes || "",
    }, type));

    // ---- STEP 2: SYNCHRONOUS window.open BEFORE any async work ----
    const win = window.open(waUrl, "_blank");
    const popupBlocked = !win;

    // ---- STEP 3: fire tracking (non-blocking) ----
    try {
      track({
        custom: ["QuoteSentToWhatsApp", "WhatsAppClicked"],
        params: {
          type,
          estimate_min: estimate?.min,
          estimate_max: estimate?.max,
          value: estimate?.min,
          currency: "EUR",
        },
      });
    } catch (_) {}
    try {
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-18132555642/XmO9CKa8-9IcEPquo8ZD",
          value: estimate?.min || undefined,
          currency: "EUR",
        });
      }
    } catch (_) {}
    try {
      if (typeof window.fbq === "function") {
        window.fbq("trackCustom", "QuoteFormSubmitted", {
          value: estimate?.min || undefined,
          currency: "EUR",
          content_name: type === "small" ? "Kleine Transporte" : "Kompletter Umzug",
        });
      }
    } catch (_) {}

    // ---- STEP 4: fire-and-forget POST to persist lead in DB ----
    api.post("/quotes", {
      type,
      pickup_address: form.pickup_address,
      delivery_address: form.delivery_address,
      pickup_floor: +form.pickup_floor || 0,
      pickup_elevator: form.pickup_elevator,
      delivery_floor: +form.delivery_floor || 0,
      delivery_elevator: form.delivery_elevator,
      moving_date: form.moving_date,
      moving_time: form.moving_time || null,
      time_window: form.time_window || form.moving_time || null,
      first_name: form.first_name,
      last_name: form.last_name,
      phone: form.phone,
      whatsapp: form.whatsapp || form.phone,
      photo_ids: form.photos.map((p) => p.id),
      detected_items: form.detected_items || [],
      job_type: form.job_type || null,
      effort: form.effort || null,
    }).catch(() => { /* lead is captured via WhatsApp anyway */ });

    try { localStorage.removeItem(DRAFT_KEY); } catch {}

    // ---- STEP 5: persist wa URL for thank-you fallback + navigate ----
    try {
      sessionStorage.setItem("leukas_last_wa", JSON.stringify({ url: waUrl, popupBlocked }));
    } catch (_) {}
    window.location.href = "/danke-umzug";
  };

  // Build absolute photo URLs (used in WhatsApp message text)
  const photoUrls = (form.photos || []).map((p) => {
    if (!p?.url) return "";
    if (p.url.startsWith("http")) return p.url;
    return `${BACKEND_URL}${p.url.startsWith("/") ? "" : "/"}${p.url}`;
  }).filter(Boolean);

  // Pre-computed quote-like object for an immediate, user-gesture-safe WhatsApp link
  const previewQuote = {
    type,
    first_name: form.first_name,
    last_name: form.last_name,
    phone: form.phone,
    whatsapp: form.whatsapp || form.phone,
    pickup_address: form.pickup_address,
    delivery_address: form.delivery_address,
    pickup_floor: +form.pickup_floor || 0,
    pickup_elevator: form.pickup_elevator,
    delivery_floor: +form.delivery_floor || 0,
    delivery_elevator: form.delivery_elevator,
    moving_date: form.moving_date,
    moving_time: form.moving_time,
    time_window: form.time_window,
    estimate_min: estimate?.min,
    estimate_max: estimate?.max,
    photo_ids: form.photos.map((p) => p.id),
    photo_urls: photoUrls,
    photo_count: form.photos.length,
    detected_items: form.detected_items || [],
    job_type: form.job_type || undefined,
    effort: form.effort || undefined,
  };
  const previewLink = estimate
    ? whatsappLink(waNumber, buildWhatsappMessage(previewQuote, "Leukas Umzug Service"))
    : "#";

  return (
    <div className="min-h-screen flex flex-col">
      <SiteHeader />
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Type chip + Schritt-Indikator */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
            {type === "small" ? <Package size={14} /> : <HomeIcon size={14} />}
            {type === "small" ? "Kleine Transporte" : "Kompletter Umzug"}
          </div>
          <div className="text-xs sm:text-sm font-semibold text-slate-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1" data-testid="step-counter">
            Schritt {step + 1} von {STEPS.length}
          </div>
        </div>

        {/* Stepper */}
        <div className="mt-3 mb-6">
          <div className="flex items-center justify-between gap-1" data-testid="stepper">
            {STEPS.map((s, i) => (
              <React.Fragment key={i}>
                <div className="flex flex-col items-center min-w-0">
                  <div className={`w-9 h-9 rounded-full grid place-items-center text-xs font-bold border-2 transition-all ${
                    i < step ? "bg-brand-amber text-brand-navy border-brand-amber"
                    : i === step ? "bg-brand-navy text-white border-brand-navy"
                    : "bg-white text-slate-400 border-slate-200"
                  }`}>
                    {i < step ? <Check size={16} /> : i + 1}
                  </div>
                  <span className={`mt-1 text-[10px] sm:text-xs font-semibold ${i === step ? "text-slate-900" : "text-slate-400"}`}>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`flex-1 h-0.5 mt-[-18px] rounded ${i < step ? "bg-brand-amber" : "bg-slate-200"}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="rounded-3xl bg-white border border-slate-200 shadow-soft p-5 sm:p-8 animate-fade-up">
          {step === 0 && (
            <div className="space-y-5" data-testid="step-0">
              <h2 className="text-2xl font-heading font-bold text-slate-900">Adressen</h2>
              <p className="text-slate-600 text-sm">Wo soll abgeholt und wo geliefert werden?</p>

              <Field label="Abholadresse" testid="pickup-address">
                <AddressAutocomplete
                  placeholder="Straße, Hausnr., PLZ, Ort"
                  value={form.pickup_address}
                  onChange={(v) => update({ pickup_address: v })}
                  testid="input-pickup"
                />
              </Field>
              <Field label="Lieferadresse" testid="delivery-address">
                <AddressAutocomplete
                  placeholder="Straße, Hausnr., PLZ, Ort"
                  value={form.delivery_address}
                  onChange={(v) => update({ delivery_address: v })}
                  testid="input-delivery"
                />
              </Field>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6" data-testid="step-1">
              <h2 className="text-2xl font-heading font-bold text-slate-900">Etagen & Aufzug</h2>
              <FloorBlock
                title="Abholung"
                floor={form.pickup_floor}
                elevator={form.pickup_elevator}
                onFloor={(v) => update({ pickup_floor: v })}
                onElevator={(v) => update({ pickup_elevator: v })}
                idPrefix="pickup"
              />
              <FloorBlock
                title="Lieferung"
                floor={form.delivery_floor}
                elevator={form.delivery_elevator}
                onFloor={(v) => update({ delivery_floor: v })}
                onElevator={(v) => update({ delivery_elevator: v })}
                idPrefix="delivery"
              />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="step-2">
              <h2 className="text-2xl font-heading font-bold text-slate-900">Termin</h2>
              <Field label="Wunschdatum" testid="moving-date">
                <input
                  type="date"
                  className="input"
                  value={form.moving_date}
                  onChange={(e) => update({ moving_date: e.target.value })}
                  min={new Date().toISOString().slice(0, 10)}
                  data-testid="input-date"
                />
              </Field>
              <Field label="Wunschuhrzeit" testid="moving-time">
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {TIME_SLOTS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => update({ moving_time: form.moving_time === t ? "" : t })}
                      className={`rounded-xl px-2 py-3 text-sm font-semibold border transition-colors ${
                        form.moving_time === t ? "bg-brand-navy text-white border-brand-navy" : "bg-white border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`time-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Clock size={14} className="text-slate-400" />
                  <input
                    type="time"
                    value={TIME_SLOTS.includes(form.moving_time) ? "" : form.moving_time}
                    onChange={(e) => update({ moving_time: e.target.value })}
                    className="input max-w-[150px]"
                    placeholder="Eigene Zeit"
                    data-testid="time-custom"
                  />
                  <span className="text-xs text-slate-500">oder eigene Uhrzeit wählen</span>
                </div>
              </Field>
              <Field label="Zeitfenster (optional)" testid="time-window">
                <div className="grid grid-cols-3 gap-2">
                  {["Vormittag", "Nachmittag", "Flexibel"].map((tw) => (
                    <button
                      key={tw}
                      type="button"
                      onClick={() => update({ time_window: form.time_window === tw ? "" : tw })}
                      className={`rounded-xl px-3 py-3 text-sm font-semibold border transition-colors ${
                        form.time_window === tw ? "bg-brand-navy text-white border-brand-navy" : "bg-white border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`tw-${tw}`}
                    >
                      {tw}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4" data-testid="step-3">
              <h2 className="text-2xl font-heading font-bold text-slate-900">Fotos hochladen</h2>
              <p className="text-slate-600 text-sm">
                Je mehr Fotos Sie hochladen, desto genauer wird Ihr Angebot.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <label className="cursor-pointer rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50/50 hover:bg-amber-50 py-7 grid place-items-center text-center transition-colors">
                  <input type="file" accept="image/*" capture="environment" multiple onChange={(e) => onPickFiles(e.target.files)} className="hidden" data-testid="quote-camera" />
                  <Camera className="text-brand-amberDark mb-1" size={24} />
                  <div className="text-sm font-semibold">Kamera</div>
                </label>
                <label className="cursor-pointer rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/70 hover:bg-slate-100 py-7 grid place-items-center text-center transition-colors">
                  <input type="file" accept="image/*" multiple onChange={(e) => onPickFiles(e.target.files)} className="hidden" data-testid="quote-upload" />
                  <Upload className="text-slate-700 mb-1" size={24} />
                  <div className="text-sm font-semibold">Galerie</div>
                </label>
              </div>
              {uploading && (
                <div className="flex items-center gap-2 text-sm text-slate-600"><Loader2 className="animate-spin" size={16} /> Hochladen…</div>
              )}
              {form.photos.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="photo-thumbs">
                  {form.photos.map((p) => (
                    <div key={p.id} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200">
                      <img src={fileUrl(p.url)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => removePhoto(p.id)} className="absolute top-1 right-1 bg-white/90 hover:bg-white rounded-lg p-1 shadow-soft" data-testid={`remove-${p.id}`}>
                        <Trash2 size={14} className="text-red-600" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5" data-testid="step-4">
              <h2 className="text-2xl font-heading font-bold text-slate-900">Ihre Kontaktdaten</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Vorname" testid="first-name">
                  <input className="input" value={form.first_name} onChange={(e) => update({ first_name: e.target.value })} data-testid="input-first" />
                </Field>
                <Field label="Nachname" testid="last-name">
                  <input className="input" value={form.last_name} onChange={(e) => update({ last_name: e.target.value })} data-testid="input-last" />
                </Field>
              </div>
              <Field label="Telefonnummer" testid="phone">
                <input className="input" placeholder="+49 …" value={form.phone} onChange={(e) => update({ phone: e.target.value })} data-testid="input-phone" />
                <div className="text-xs text-slate-500 mt-1">Ihr Angebot wird über WhatsApp beantwortet.</div>
              </Field>
              <Field label="WhatsApp-Nummer (falls abweichend)" testid="whatsapp">
                <input className="input" placeholder="optional" value={form.whatsapp} onChange={(e) => update({ whatsapp: e.target.value })} data-testid="input-whatsapp" />
              </Field>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5" data-testid="step-5">
              <h2 className="text-2xl font-heading font-bold text-slate-900">Ihr Angebot</h2>
              {estimate ? (
                <div className="rounded-2xl bg-amber-50 border border-amber-200 p-6">
                  <div className="text-xs font-bold uppercase tracking-widest text-brand-amberDark">Geschätztes Angebot</div>
                  <div className="mt-1 text-4xl font-heading font-extrabold text-slate-900" data-testid="estimate-display">
                    {estimate.min}€ – {estimate.max}€
                  </div>
                  <p className="mt-2 text-sm text-slate-700">
                    Der endgültige Preis wird nach kurzer Prüfung Ihrer Fotos über WhatsApp bestätigt.
                  </p>
                </div>
              ) : (
                <div className="text-slate-600">Schätzung wird berechnet…</div>
              )}

              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-5 text-sm text-slate-700 space-y-1.5">
                <div><b>Typ:</b> {type === "small" ? "Kleine Transporte" : "Kompletter Umzug"}</div>
                <div><b>Abholung:</b> {form.pickup_address} · Etage {form.pickup_floor} · Aufzug {form.pickup_elevator ? "Ja" : "Nein"}</div>
                <div><b>Lieferung:</b> {form.delivery_address} · Etage {form.delivery_floor} · Aufzug {form.delivery_elevator ? "Ja" : "Nein"}</div>
                <div><b>Datum:</b> {form.moving_date} {form.moving_time && `· ${form.moving_time} Uhr`} {form.time_window && `(${form.time_window})`}</div>
                <div><b>Kontakt:</b> {form.first_name} {form.last_name} · {form.phone}</div>
                <div><b>Fotos:</b> {form.photos.length}</div>
                {form.detected_items?.length > 0 && (
                  <div><b>Erkannte Gegenstände:</b> {form.detected_items.join(", ")}</div>
                )}
                {form.job_type && (
                  <div><b>Auftrag-Typ:</b> {form.job_type}</div>
                )}
                {form.effort && (
                  <div><b>Aufwand:</b> {form.effort}</div>
                )}
              </div>

              {!createdQuote ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      if (!estimate) return;
                      submitQuote();
                    }}
                    disabled={!estimate}
                    className={`w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-brand-amber text-brand-navy px-6 py-4 font-extrabold shadow-soft hover:bg-amber-300 transition-colors ${!estimate ? "opacity-60 pointer-events-none" : ""}`}
                    data-testid="send-whatsapp"
                  >
                    <MessageCircle size={18} />
                    Anfrage per WhatsApp senden
                  </button>
                  <p className="mt-2 text-center text-[12px] text-slate-500">
                    Ihre Daten werden automatisch in WhatsApp eingetragen – Sie müssen nur noch auf Senden tippen.
                  </p>
                </>
              ) : (
                <div className="rounded-2xl bg-green-50 border border-green-200 p-5" data-testid="quote-success">
                  <div className="font-heading font-bold text-green-800">Angebot gesendet</div>
                  <p className="text-sm text-green-700 mt-1">
                    Bitte schließen Sie die WhatsApp-Nachricht im neuen Tab. Sollte sich WhatsApp nicht
                    geöffnet haben, klicken Sie unten erneut.
                  </p>
                  <a
                    href={whatsappLink(waNumber, buildWhatsappMessage({ ...createdQuote, photo_urls: photoUrls }))}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-green-800 hover:underline"
                    data-testid="quote-reopen-wa"
                  >
                    WhatsApp erneut öffnen <ArrowRight size={14} />
                  </a>
                  <div className="mt-4">
                    <Link to="/" className="text-sm text-slate-600 hover:text-slate-900">← Zurück zur Startseite</Link>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer nav */}
          {step < STEPS.length - 1 ? (
            <div className="mt-8 flex justify-between gap-3">
              <button
                onClick={back}
                disabled={step === 0}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 disabled:opacity-40 hover:bg-slate-50 transition-colors"
                data-testid="step-back"
              >
                <ArrowLeft size={16} /> Zurück
              </button>
              <button
                onClick={next}
                className="inline-flex items-center gap-2 rounded-xl bg-brand-navy text-white px-5 py-3 text-sm font-semibold hover:bg-slate-800 transition-colors"
                data-testid="step-next"
              >
                Weiter <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            !createdQuote && (
              <div className="mt-6 flex justify-start">
                <button
                  onClick={back}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                  data-testid="step-back-final"
                >
                  <ArrowLeft size={16} /> Daten ändern
                </button>
              </div>
            )
          )}
        </div>
      </main>
      <SiteFooter />
      <style>{`.input { width:100%; border:1px solid #e2e8f0; border-radius: 12px; padding: 12px 14px; outline:none; transition: border-color .15s ease, box-shadow .15s ease; background: white; }
        .input:focus { border-color: #F59E0B; box-shadow: 0 0 0 4px rgba(245,158,11,0.12); }`}</style>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid ? `field-${testid}` : undefined}>
      <label className="block text-sm font-semibold text-slate-700 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function FloorBlock({ title, floor, elevator, onFloor, onElevator, idPrefix }) {
  const floors = [0, 1, 2, 3, 4, 5];
  return (
    <div className="rounded-2xl border border-slate-200 p-4">
      <div className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">{title}</div>
      <div>
        <div className="text-sm font-semibold text-slate-700 mb-2">Etage</div>
        <div className="flex flex-wrap gap-2">
          {floors.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => onFloor(f)}
              className={`min-w-[44px] rounded-xl px-3 py-2 text-sm font-semibold border transition-colors ${
                +floor === f ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              }`}
              data-testid={`${idPrefix}-floor-${f}`}
            >
              {f === 0 ? "EG" : f + ". OG"}
            </button>
          ))}
          <input
            type="number"
            min={0}
            max={20}
            value={floor}
            onChange={(e) => onFloor(+e.target.value || 0)}
            className="w-20 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700"
            data-testid={`${idPrefix}-floor-input`}
          />
        </div>
      </div>
      <div className="mt-4">
        <div className="text-sm font-semibold text-slate-700 mb-2">Aufzug?</div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onElevator(true)}
            className={`flex-1 rounded-xl px-3 py-3 text-sm font-semibold border transition-colors ${
              elevator ? "bg-brand-amber text-brand-navy border-brand-amber" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`${idPrefix}-elev-yes`}
          >Ja</button>
          <button
            type="button"
            onClick={() => onElevator(false)}
            className={`flex-1 rounded-xl px-3 py-3 text-sm font-semibold border transition-colors ${
              !elevator ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`${idPrefix}-elev-no`}
          >Nein</button>
        </div>
      </div>
    </div>
  );
}
